const BC = { fontFamily: "'Oswald', sans-serif" } as const;

interface Cta {
  label: string;
  href: string;
}

interface PageHeroProps {
  eyebrow: string;
  titleSolid: string;
  titleOutline: string;
  subtitle: string;
  primaryCta: Cta;
  secondaryCta?: Cta;
  ghostText: string;
  titleSize?: string;
}

export default function PageHero({
  eyebrow,
  titleSolid,
  titleOutline,
  subtitle,
  primaryCta,
  secondaryCta,
  ghostText,
  titleSize = "clamp(4.5rem, 11vw, 10rem)",
}: PageHeroProps) {
  return (
    <section
      className="relative min-h-screen flex flex-col justify-end px-6 md:px-10 pb-20 overflow-hidden"
      style={{
        background:
          "linear-gradient(to top, #0b0b0b 0%, rgba(11,11,11,0.5) 55%, transparent 100%), " +
          "linear-gradient(160deg, #150a10 0%, #0b0b0b 45%, #080c14 100%)",
      }}
    >
      {/* Glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 75% 35%, rgba(255,46,137,0.07) 0%, transparent 55%), " +
            "radial-gradient(ellipse at 15% 75%, rgba(30,30,80,0.35) 0%, transparent 50%)",
        }}
      />

      {/* Ghost watermark */}
      <span
        className="absolute right-[-0.05em] bottom-[0.05em] text-transparent pointer-events-none select-none leading-none font-black uppercase hidden md:block"
        style={{
          ...BC,
          fontSize: "clamp(10rem, 22vw, 20rem)",
          WebkitTextStroke: "1px rgba(255,255,255,0.04)",
        }}
      >
        {ghostText}
      </span>

      {/* Content */}
      <div className="relative z-10 max-w-5xl">
        <p
          className="text-[#ff2e89] text-xs font-bold tracking-[0.32em] uppercase mb-4"
          style={BC}
        >
          {eyebrow}
        </p>

        <h1
          className="font-black uppercase mb-6 leading-[0.88]"
          style={{ ...BC, fontSize: titleSize, letterSpacing: "-0.01em" }}
        >
          <span className="text-[#f0ece8] block" style={BC}>
            {titleSolid}
          </span>
          <span
            className="text-transparent block"
            style={{ ...BC, WebkitTextStroke: "2px #f0ece8" }}
          >
            {titleOutline}
          </span>
        </h1>

        <p className="text-[#888] text-lg leading-relaxed max-w-[540px] mb-10">
          {subtitle}
        </p>

        <div className="flex gap-4 items-center flex-wrap">
          <a
            href={primaryCta.href}
            className="no-underline inline-block uppercase font-bold tracking-widest text-base bg-[#ff2e89] text-[#0b0b0b] px-9 py-3 hover:opacity-80 transition-opacity"
            style={BC}
          >
            {primaryCta.label}
          </a>
          {secondaryCta && (
            <a
              href={secondaryCta.href}
              className="no-underline inline-block uppercase font-bold tracking-widest text-base border border-[#242424] text-[#f0ece8] px-9 py-3 hover:border-[rgba(240,236,232,0.4)] transition-colors"
              style={BC}
            >
              {secondaryCta.label}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
